import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Card, Spinner, EmptyState } from "@/components/bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Undo2, Loader2 } from "lucide-react";

const REASONS = ["Salah Part", "Rusak", "Tidak Sesuai", "Cancel SPK", "Duplicate Supply", "Return to Insurance", "Lainnya"];

export default function PartRetur() {
  const [reserved, setReserved] = useState([]);
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ret, setRet] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/warehouse/reserved"), api.get("/returns")])
      .then(([r, x]) => { setReserved(r.data.filter((i) => i.remaining_qty > 0)); setReturns(x.data); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const submit = async () => {
    if (!ret.reason) return toast.error("Pilih alasan");
    setSaving(true);
    try {
      await api.post("/returns", { job_id: ret.job_id, part_number: ret.part_number, part_name: ret.part_name, qty: Number(ret.qty), reason: ret.reason, supplier: ret.supplier, notes: ret.notes });
      toast.success("Retur tercatat"); setRet(null); load();
    } catch (e) { toast.error(apiError(e, "Gagal retur")); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Part Retur" subtitle="Pilih part dari stok warehouse untuk diretur (tanpa input ulang)." />
      {loading ? <Spinner /> : (
        <>
          <h3 className="font-head font-semibold text-gray-900 mb-2">Stok Tersedia untuk Retur</h3>
          {reserved.length === 0 ? <EmptyState title="Tidak ada stok tersedia" icon={Undo2} /> : (
            <div className="grid gap-2 mb-6">
              {reserved.map((r, i) => (
                <Card key={i} className="p-3 flex items-center justify-between gap-2" data-testid={`retur-avail-${i}`}>
                  <div>
                    <div className="font-mono-data font-semibold">{r.nopol} • {r.part_number}</div>
                    <div className="text-sm text-gray-600">{r.part_name} • Available {r.remaining_qty}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setRet({ ...r, qty: r.remaining_qty, reason: "", supplier: "", notes: "" })} data-testid={`retur-btn-${i}`}><Undo2 size={14} className="mr-1" />Retur</Button>
                </Card>
              ))}
            </div>
          )}
          <h3 className="font-head font-semibold text-gray-900 mb-2">Riwayat Retur</h3>
          {returns.length === 0 ? <div className="text-sm text-gray-400">Belum ada retur.</div> : (
            <div className="grid gap-2">
              {returns.map((r) => (
                <Card key={r.id} className="p-3 text-sm flex justify-between"><span className="font-mono-data font-semibold">{r.nopol} • {r.part_number} ×{r.qty}</span><span className="text-gray-500">{r.reason}</span></Card>
              ))}
            </div>
          )}
        </>
      )}

      <Dialog open={!!ret} onOpenChange={(o) => !o && setRet(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head">Retur Part</DialogTitle></DialogHeader>
          {ret && (
            <div className="space-y-3">
              <div className="text-sm"><b className="font-mono-data">{ret.part_number}</b> {ret.part_name} (avail {ret.remaining_qty})</div>
              <div><label className="text-xs uppercase text-gray-500">Qty Retur</label><Input type="number" min={1} max={ret.remaining_qty} value={ret.qty} onChange={(e) => setRet({ ...ret, qty: e.target.value })} className="mt-1" data-testid="retur-qty" /></div>
              <div><label className="text-xs uppercase text-gray-500">Alasan</label>
                <Select value={ret.reason} onValueChange={(v) => setRet({ ...ret, reason: v })}>
                  <SelectTrigger className="mt-1" data-testid="retur-reason"><SelectValue placeholder="Pilih alasan" /></SelectTrigger>
                  <SelectContent>{REASONS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="text-xs uppercase text-gray-500">Supplier</label><Input value={ret.supplier} onChange={(e) => setRet({ ...ret, supplier: e.target.value })} className="mt-1" /></div>
              <div><label className="text-xs uppercase text-gray-500">Keterangan</label><Input value={ret.notes} onChange={(e) => setRet({ ...ret, notes: e.target.value })} className="mt-1" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRet(null)}>Batal</Button>
            <Button onClick={submit} disabled={saving} className="bg-gray-900" data-testid="confirm-retur-btn">{saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}Confirm Retur</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
